import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';

import {
  createDefaultIdleStateDraft,
  ExpandedAgentIdleState,
  ExpandedAgentIdleStateDraft,
  IdleStateValidationErrors,
} from '../model/idleState';
import { validateIdleStateDraft } from '../utils/idle-state-validation';

export const useAgentIdleStateController = () => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<ExpandedAgentIdleStateDraft>(createDefaultIdleStateDraft());
  const [errors, setErrors] = useState<IdleStateValidationErrors>({});
  const [baselineVersion, setBaselineVersion] = useState(0);
  const baselineRef = useRef<ExpandedAgentIdleStateDraft>(createDefaultIdleStateDraft());

  const hydrate = useCallback((nextDraft: ExpandedAgentIdleStateDraft) => {
    baselineRef.current = nextDraft;
    setDraft(nextDraft);
    setErrors({});
  }, []);

  const update = useCallback((patch: Partial<ExpandedAgentIdleStateDraft>) => {
    setDraft((previous) => ({ ...previous, ...patch }));
    setErrors((previous) => {
      const nextErrors = { ...previous };
      Object.keys(patch).forEach((key) => {
        delete nextErrors[key as keyof IdleStateValidationErrors];
      });
      return nextErrors;
    });
  }, []);

  const validate = useCallback((): ExpandedAgentIdleState | null => {
    const result = validateIdleStateDraft(draft, t);
    setErrors(result.errors);
    return result.value;
  }, [draft, t]);

  const commit = useCallback(() => {
    baselineRef.current = draft;
    setBaselineVersion((version) => version + 1);
  }, [draft]);

  const isDirty = useMemo(() => !isEqual(draft, baselineRef.current), [baselineVersion, draft]);

  return {
    draft,
    errors,
    isDirty,
    hydrate,
    update,
    validate,
    commit,
  };
};
